import { useCallback, useEffect, useRef, useState } from 'react'
import KpiInfoTooltip from './KpiInfoTooltip'

const COMPARE_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'mom', label: 'MoM' },
  { value: 'yoy', label: 'YoY' },
]

const SOURCE_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'organic', label: 'Organic' },
  { value: 'paid', label: 'Paid' },
]

function formatMonthlySocialComparison(value, comparisonType) {
  if (comparisonType === 'none') return null
  if (value == null || Number.isNaN(Number(value))) return '—'
  const rounded = Math.round(Number(value) * 10) / 10
  const suffix = comparisonType === 'yoy' ? 'YoY' : 'MoM'
  if (rounded === 0) return `0% ${suffix}`
  const prefix = rounded > 0 ? '+' : ''
  return `${prefix}${rounded}% ${suffix}`
}

function monthlySocialDeltaClass(comparisonValue, comparisonLabel) {
  if (comparisonLabel == null || comparisonLabel === '—') return 'monthlySocialCellDelta--empty'
  if (comparisonValue > 0) return 'monthlySocialCellDelta--up'
  if (comparisonValue < 0) return 'monthlySocialCellDelta--down'
  return 'monthlySocialCellDelta--up'
}

function monthlySocialRowKey(row, index) {
  const typeId = row?.socialNetworkTypeId
  if (typeId != null && typeId !== '' && !Number.isNaN(Number(typeId))) {
    return `monthly-social-${Number(typeId)}-${index}`
  }
  if (row?.platform) return `monthly-social-${row.platform}-${index}`
  return `monthly-social-row-${index}`
}

export function MonthlySocialPerformanceFilterBar({
  comparePeriod,
  onComparePeriodChange,
  source,
  onSourceChange,
  compareFilterClassName = 'platformFilterDropdown platformFilterDropdown--persistent',
  showSourceDropdown = true,
}) {
  const [compareFilterOpen, setCompareFilterOpen] = useState(false)
  const [sourceFilterOpen, setSourceFilterOpen] = useState(false)
  const compareFilterRef = useRef(null)
  const sourceFilterRef = useRef(null)

  useEffect(() => {
    if (!compareFilterOpen && !sourceFilterOpen) return undefined
    const onDoc = (e) => {
      if (compareFilterRef.current?.contains(e.target)) return
      if (sourceFilterRef.current?.contains(e.target)) return
      setCompareFilterOpen(false)
      setSourceFilterOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [compareFilterOpen, sourceFilterOpen])

  return (
    <>
      <div ref={compareFilterRef} className={`dashboardFilterSelect ${compareFilterClassName}`}>
        <button
          className="dashboardFilterSelectButton"
          type="button"
          onClick={() => setCompareFilterOpen((v) => !v)}
          aria-label="Comparison period"
          aria-expanded={compareFilterOpen}
        >
          <span className="dashboardFilterSelectLabel">Compare</span>
          <span className="dashboardFilterSelectValue">
            {COMPARE_OPTIONS.find((o) => o.value === comparePeriod)?.label ?? 'None'}
          </span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
        {compareFilterOpen && (
          <div className="dashboardFilterSelectMenu">
            {COMPARE_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`dashboardFilterSelectOption ${comparePeriod === option.value ? 'dashboardFilterSelectOption--active' : ''}`}
                onClick={() => {
                  onComparePeriodChange(option.value)
                  setCompareFilterOpen(false)
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="platformFilterTabs" role="tablist" aria-label="Traffic source">
        {SOURCE_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            className={`platformFilterTab ${source === option.value ? 'platformFilterTab--active' : ''}`}
            onClick={() => onSourceChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
      {showSourceDropdown && (
        <div ref={sourceFilterRef} className="dashboardFilterSelect platformFilterDropdown">
          <button
            className="dashboardFilterSelectButton"
            type="button"
            onClick={() => setSourceFilterOpen((v) => !v)}
          >
            <span className="dashboardFilterSelectLabel">Source</span>
            <span className="dashboardFilterSelectValue">
              {SOURCE_OPTIONS.find((o) => o.value === source)?.label ?? 'All'}
            </span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          {sourceFilterOpen && (
            <div className="dashboardFilterSelectMenu">
              {SOURCE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`dashboardFilterSelectOption ${source === option.value ? 'dashboardFilterSelectOption--active' : ''}`}
                  onClick={() => {
                    onSourceChange(option.value)
                    setSourceFilterOpen(false)
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default function MonthlySocialPerformancePanel({
  columns = [],
  rowsBySource = { all: [], organic: [], paid: [] },
  resolveIconUrl,
  comparePeriod: comparePeriodProp,
  onComparePeriodChange,
  source: sourceProp,
  onSourceChange,
  hideFilterBar = false,
}) {
  const [comparePeriodInternal, setComparePeriodInternal] = useState('mom')
  const [sourceInternal, setSourceInternal] = useState('all')

  const comparePeriod = comparePeriodProp ?? comparePeriodInternal
  const setComparePeriod = onComparePeriodChange ?? setComparePeriodInternal
  const source = sourceProp ?? sourceInternal
  const setSource = onSourceChange ?? setSourceInternal

  const tableScrollRef = useRef(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const [isScrollable, setIsScrollable] = useState(false)

  const activeRows = rowsBySource?.[source] ?? []
  const rowsWithIcons = (activeRows || [])
    .filter((row) => !row?.empty)
    .map((row) => {
      const icon = resolveIconUrl?.(row)
      return icon ? { ...row, icon } : row
    })
  const showEmpty = rowsWithIcons.length === 0

  const updateScroll = useCallback(() => {
    const node = tableScrollRef.current
    if (!node) return
    const { scrollLeft, scrollWidth, clientWidth } = node
    setIsScrollable(scrollWidth > clientWidth + 1)
    setCanScrollLeft(scrollLeft > 4)
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 4)
  }, [])

  useEffect(() => {
    const node = tableScrollRef.current
    if (!node) return undefined
    updateScroll()
    const observer = new ResizeObserver(updateScroll)
    observer.observe(node)
    node.addEventListener('scroll', updateScroll, { passive: true })
    window.addEventListener('resize', updateScroll)
    return () => {
      observer.disconnect()
      node.removeEventListener('scroll', updateScroll)
      window.removeEventListener('resize', updateScroll)
    }
  }, [updateScroll, rowsWithIcons.length, columns.length, source])

  const scrollTable = (direction) => {
    const node = tableScrollRef.current
    if (!node) return
    const delta = direction === 'left' ? -280 : 280
    node.scrollBy({ left: delta, behavior: 'smooth' })
  }

  return (
    <>
      {!hideFilterBar && (
        <div className="monthlySocialPanelFilters">
          <MonthlySocialPerformanceFilterBar
            comparePeriod={comparePeriod}
            onComparePeriodChange={setComparePeriod}
            source={source}
            onSourceChange={setSource}
          />
        </div>
      )}

      {showEmpty ? (
        <div className="monthlySocialEmpty" role="status">
          No data available
        </div>
      ) : (
        <div className="monthlySocialTableFrame">
        <div className="monthlySocialTablePinned">
          <table className="monthlySocialTable monthlySocialTable--pinned">
            <thead>
              <tr className="monthlySocialTableRow monthlySocialTableRow--header">
                <th className="monthlySocialCell monthlySocialCell--label" scope="col">
                  <div className="monthlySocialCellContent monthlySocialCellContent--label">
                    Platform
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {rowsWithIcons.map((row, index) => (
                <tr key={monthlySocialRowKey(row, index)} className="monthlySocialTableRow">
                  <th className="monthlySocialCell monthlySocialCell--label" scope="row">
                    <div className="monthlySocialCellContent monthlySocialCellContent--label">
                      <div className="monthlySocialPlatformLabel">
                        {row.icon && (
                          <span className="monthlySocialPlatformIcon">
                            <img src={row.icon} alt="" />
                          </span>
                        )}
                        <span className="monthlySocialPlatformName">{row.label}</span>
                      </div>
                    </div>
                  </th>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div
          className={`monthlySocialTableScrollWrap${
            isScrollable ? ' monthlySocialTableScrollWrap--scrollable' : ''
          }`}
        >
          {isScrollable && canScrollLeft && (
            <button
              type="button"
              className="monthlySocialScrollHint monthlySocialScrollHint--left"
              onClick={() => scrollTable('left')}
              aria-label="Scroll metrics left"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
          {isScrollable && canScrollRight && (
            <button
              type="button"
              className="monthlySocialScrollHint monthlySocialScrollHint--right"
              onClick={() => scrollTable('right')}
              aria-label="Scroll metrics right"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
          <div
            className="monthlySocialTableScroll"
            ref={tableScrollRef}
            aria-label="Monthly social metrics (scroll horizontally)"
          >
            <table className="monthlySocialTable monthlySocialTable--metrics">
              <thead>
                <tr className="monthlySocialTableRow monthlySocialTableRow--header">
                  {columns.map((column) => (
                    <th key={column.key} className="monthlySocialCell monthlySocialCell--header" scope="col">
                      <div className="monthlySocialCellContent monthlySocialCellContent--header">
                        <span className="monthlySocialColumnLabel">{column.label}</span>
                        {column.info && (
                          <KpiInfoTooltip
                            content={column.info}
                            aria-label={`${column.label} definition`}
                            className="monthlySocialColumnInfo"
                            portaled
                            placement="bottom"
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                          />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rowsWithIcons.map((row, index) => (
                  <tr key={monthlySocialRowKey(row, index)} className="monthlySocialTableRow">
                    {columns.map((column) => {
                      const comparisonValue =
                        comparePeriod === 'none'
                          ? null
                          : row.comparisons?.[comparePeriod]?.[column.key]
                      const comparisonLabel = formatMonthlySocialComparison(comparisonValue, comparePeriod)
                      return (
                        <td key={column.key} className="monthlySocialCell">
                          <div className="monthlySocialCellContent">
                            <div className="monthlySocialCellValue dashboard-tabular-nums">
                              {row.values[column.key]}
                            </div>
                            <div
                              className={`monthlySocialCellDelta dashboard-tabular-nums ${monthlySocialDeltaClass(
                                comparisonValue,
                                comparisonLabel,
                              )}`}
                              aria-hidden={comparisonLabel == null}
                            >
                              {comparisonLabel ?? '\u00a0'}
                            </div>
                          </div>
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        </div>
      )}
    </>
  )
}
